// ─────────────────────────────────────────────────────────────────────────────
// KLOTHIQ — Operational Status Utilities (FEAT-SALE-SMART-STATUS-DASHBOARD-001)
// ─────────────────────────────────────────────────────────────────────────────
// Maps the 3 orthogonal database columns (sale_status, payment_status, return_status)
// to the 10 core operational retail statuses requested by merchants:
//   1. Booked
//   2. Confirmed
//   3. Delivered
//   4. Payment pending
//   5. Payment completed
//   6. Return request
//   7. Return accepted
//   8. Return completed
//   9. Refund pending
//  10. Refund completed
// ─────────────────────────────────────────────────────────────────────────────

import type { Database, SaleFinancialSummary } from '@klothiq/domain';

export type SaleRow = Database['public']['Tables']['sales']['Row'] & {
  financial_summary?: SaleFinancialSummary;
  customers?: { name: string; phone: string | null } | null;
  balance_due?: number | string | null;
  total_amount?: number | string | null;
  paid_amount?: number | string | null;
};

export type StatusCategory = 'fulfillment' | 'payment' | 'return_refund';

export interface OperationalStatusDef {
  id: string;
  label: string;
  category: StatusCategory;
  color: 'amber' | 'blue' | 'green' | 'red' | 'grey';
  description: string;
}

export const OPERATIONAL_STATUSES: OperationalStatusDef[] = [
  // Fulfillment Axis
  {
    id: 'booked',
    label: 'Booked',
    category: 'fulfillment',
    color: 'amber',
    description: 'Stock reserved for customer',
  },
  {
    id: 'confirmed',
    label: 'Confirmed',
    category: 'fulfillment',
    color: 'blue',
    description: 'Verified & ready for dispatch',
  },
  {
    id: 'delivered',
    label: 'Delivered',
    category: 'fulfillment',
    color: 'green',
    description: 'Physically received by customer',
  },

  // Payment Axis
  {
    id: 'payment_pending',
    label: 'Payment Pending',
    category: 'payment',
    color: 'red',
    description: 'Balance due > 0 / Unpaid',
  },
  {
    id: 'payment_completed',
    label: 'Payment Completed',
    category: 'payment',
    color: 'green',
    description: 'Paid in full',
  },

  // Return & Refund Axis
  {
    id: 'return_request',
    label: 'Return Request',
    category: 'return_refund',
    color: 'amber',
    description: 'Return initiated by customer',
  },
  {
    id: 'return_accepted',
    label: 'Return Accepted',
    category: 'return_refund',
    color: 'blue',
    description: 'Return approved, awaiting item',
  },
  {
    id: 'return_completed',
    label: 'Return Completed',
    category: 'return_refund',
    color: 'green',
    description: 'Item received & restocked',
  },
  {
    id: 'refund_pending',
    label: 'Refund Review',
    category: 'return_refund',
    color: 'amber',
    description: 'Return recorded; review whether a refund is required',
  },
  {
    id: 'refund_completed',
    label: 'Refund Recorded',
    category: 'return_refund',
    color: 'green',
    description: 'A refund is recorded; check payment details for its amount',
  },
];

export interface CompoundPreset {
  id: string;
  label: string;
  statuses: string[];
  badgeColor: string;
}

export const COMPOUND_PRESETS: CompoundPreset[] = [
  {
    id: 'delivered_unpaid',
    label: 'Delivered · Payment Pending',
    statuses: ['delivered', 'payment_pending'],
    badgeColor: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
  },
  {
    id: 'delivered_paid',
    label: 'Delivered · Paid',
    statuses: ['delivered', 'payment_completed'],
    badgeColor: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  },
  {
    id: 'booked_unpaid',
    label: 'Booked · Payment Pending',
    statuses: ['booked', 'payment_pending'],
    badgeColor: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
  },
  {
    id: 'return_refund_pending',
    label: 'Return Completed · Refund Review',
    statuses: ['return_completed', 'refund_pending'],
    badgeColor: 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30',
  },
  {
    id: 'return_refund_done',
    label: 'Return & Refund Recorded',
    statuses: ['return_completed', 'refund_completed'],
    badgeColor: 'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30',
  },
];

/**
 * Evaluates all active operational tags on a given sale order
 */
export function getOperationalStatusTags(sale: SaleRow): string[] {
  const tags: string[] = [];
  const balanceDue = Number(sale.financial_summary?.amount_due ?? sale.balance_due ?? 0);
  const paymentApplicable =
    !sale.financial_summary?.return_review &&
    !['draft', 'cancelled', 'returned', 'partially_returned'].includes(sale.sale_status);
  const totalAmount = Number(sale.total_amount ?? 0);
  const paidAmount = Number(sale.financial_summary?.net_collected ?? sale.paid_amount ?? 0);

  // 1. Fulfillment tags
  if (sale.sale_status === 'booked') tags.push('booked');
  if (sale.sale_status === 'confirmed') tags.push('confirmed');
  if (sale.sale_status === 'delivered') tags.push('delivered');

  // 2. Payment tags
  if (
    paymentApplicable &&
    (['unpaid', 'partially_paid'].includes(sale.payment_status) || balanceDue > 0)
  ) {
    tags.push('payment_pending');
  } else if (
    paymentApplicable &&
    (sale.payment_status === 'paid' || (totalAmount > 0 && balanceDue <= 0 && paidAmount > 0))
  ) {
    tags.push('payment_completed');
  }

  // 3. Return & Refund tags
  if (sale.return_status === 'requested') {
    tags.push('return_request');
  } else if (sale.return_status === 'accepted') {
    tags.push('return_accepted');
  } else if (
    sale.return_status === 'completed' ||
    ['partially_returned', 'returned'].includes(sale.sale_status)
  ) {
    tags.push('return_completed');

    // Refund sub-status when return has happened
    if (['partially_refunded', 'refunded'].includes(sale.payment_status)) {
      tags.push('refund_completed');
    } else {
      tags.push('refund_pending');
    }
  }

  if (
    ['partially_refunded', 'refunded'].includes(sale.payment_status) &&
    !tags.includes('refund_completed')
  ) {
    tags.push('refund_completed');
  }

  return tags;
}

/**
 * Checks whether a sale matches the currently selected set of status filters.
 * Uses cross-category AND matching and intra-category OR matching.
 * For example: [delivered, payment_pending] => must be BOTH delivered AND payment_pending.
 */
export function matchesStatusFilters(sale: SaleRow, selectedStatusIds: string[]): boolean {
  if (!selectedStatusIds || selectedStatusIds.length === 0) return true;

  const saleTags = getOperationalStatusTags(sale);

  // Group selected filters by category
  const selectedByCategory: Record<StatusCategory, string[]> = {
    fulfillment: [],
    payment: [],
    return_refund: [],
  };

  for (const id of selectedStatusIds) {
    const def = OPERATIONAL_STATUSES.find((s) => s.id === id);
    if (def) {
      selectedByCategory[def.category].push(id);
    }
  }

  // For each active category, sale must match at least one of the selected tags in that category
  for (const category of ['fulfillment', 'payment', 'return_refund'] as StatusCategory[]) {
    const categoryFilters = selectedByCategory[category];
    if (categoryFilters.length > 0) {
      const hasMatch = categoryFilters.some((filterId) => saleTags.includes(filterId));
      if (!hasMatch) return false;
    }
  }

  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Valid Status Transitions Mapping
// ─────────────────────────────────────────────────────────────────────────────
export interface StatusTransitionOption {
  value: string;
  label: string;
  variant?: 'danger' | 'warning' | 'primary';
}

export const NEXT_STATUSES: Record<string, StatusTransitionOption[]> = {
  draft: [
    { value: 'booked', label: 'Book Order' },
    { value: 'confirmed', label: 'Confirm' },
    { value: 'delivery_pending', label: 'Delivery Pending' },
    { value: 'delivered', label: 'Mark Delivered (Immediate)' },
    { value: 'cancelled', label: 'Cancel', variant: 'danger' },
  ],
  booked: [
    { value: 'confirmed', label: 'Confirm' },
    { value: 'delivery_pending', label: 'Delivery Pending' },
    { value: 'delivered', label: 'Mark Delivered' },
    { value: 'cancelled', label: 'Cancel', variant: 'danger' },
  ],
  confirmed: [
    { value: 'booked', label: 'Revert to Booked', variant: 'warning' },
    { value: 'delivery_pending', label: 'Delivery Pending' },
    { value: 'delivered', label: 'Mark Delivered' },
    { value: 'cancelled', label: 'Cancel', variant: 'danger' },
  ],
  delivery_pending: [
    { value: 'confirmed', label: 'Revert to Confirmed', variant: 'warning' },
    { value: 'delivered', label: 'Mark Delivered' },
    { value: 'cancelled', label: 'Cancel', variant: 'danger' },
  ],
  delivered: [
    {
      value: 'delivery_pending',
      label: 'Revert to Delivery Pending (Undo Delivery)',
      variant: 'warning',
    },
    { value: 'partially_returned', label: 'Mark Partially Returned' },
    { value: 'returned', label: 'Mark Fully Returned', variant: 'warning' },
  ],
  partially_returned: [
    { value: 'delivered', label: 'Mark Delivered (Replacements / Fulfilled)' },
    { value: 'returned', label: 'Mark Fully Returned', variant: 'warning' },
  ],
  returned: [{ value: 'delivered', label: 'Revert to Delivered', variant: 'warning' }],
};

/**
 * Returns allowed next status transition options based on role permissions.
 */
export function getNextStatusOptions(
  currentStatus: string,
  role?: string | null,
): StatusTransitionOption[] {
  const isOwnerOrAdmin = ['owner', 'admin'].includes(role ?? '');
  const canChangeStatus = ['owner', 'admin', 'staff'].includes(role ?? '');
  if (!canChangeStatus) return [];

  const available = NEXT_STATUSES[currentStatus] ?? [];
  return available.filter((opt) => {
    // Reversals of delivered or returned orders require owner/admin privileges
    if (
      ['delivered', 'returned'].includes(currentStatus) &&
      ['delivery_pending', 'delivered'].includes(opt.value)
    ) {
      return isOwnerOrAdmin;
    }
    return true;
  });
}

export interface OperationalDropdownOption {
  id: string;
  label: string;
  category: 'fulfillment' | 'payment' | 'return_refund';
  group: string;
}

export const ALL_10_OPERATIONAL_STATUS_OPTIONS: OperationalDropdownOption[] = [
  // Fulfillment Axis
  { id: 'booked', label: '1. Booked', category: 'fulfillment', group: '📦 Fulfillment' },
  { id: 'confirmed', label: '2. Confirmed', category: 'fulfillment', group: '📦 Fulfillment' },
  { id: 'delivered', label: '3. Delivered', category: 'fulfillment', group: '📦 Fulfillment' },

  // Payment Axis
  { id: 'payment_pending', label: '4. Payment pending', category: 'payment', group: '💳 Payment' },
  {
    id: 'payment_completed',
    label: '5. Payment completed',
    category: 'payment',
    group: '💳 Payment',
  },

  // Return & Refund Axis
  {
    id: 'return_request',
    label: '6. Return request',
    category: 'return_refund',
    group: '🔄 Return & Refund',
  },
  {
    id: 'return_accepted',
    label: '7. Return accepted',
    category: 'return_refund',
    group: '🔄 Return & Refund',
  },
  {
    id: 'return_completed',
    label: '8. Return completed',
    category: 'return_refund',
    group: '🔄 Return & Refund',
  },
  {
    id: 'refund_pending',
    label: '9. Refund review',
    category: 'return_refund',
    group: '🔄 Return & Refund',
  },
  {
    id: 'refund_completed',
    label: '10. Refund recorded',
    category: 'return_refund',
    group: '🔄 Return & Refund',
  },
];

export const PAYMENT_STATUS_OPTIONS = [
  { value: 'unpaid', label: 'Payment Pending (Unpaid)' },
  { value: 'partially_paid', label: 'Partially Paid' },
  { value: 'paid', label: 'Payment Completed (Paid)' },
  { value: 'partially_refunded', label: 'Partially Refunded' },
  { value: 'refunded', label: 'Refunded' },
];
